import React, { useEffect, useState } from 'react';
import { Button, ColumnHeader, TextField } from '@deque/cauldron-react';
import { Trans, useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import type { v2 } from '@deque/billing-service-client';
import { useAuthContext } from '../../../../../common/contexts/auth';
import styles from './CustomSettings.css';
import { type ErrorOrNull } from '../../../../../../typings/utils';
import ScrimmedLoader from '../../../../../common/components/ScrimmedLoader';
import ContentToast from '../../../../../common/components/ContentToast';
import { useHistory, useParams } from 'react-router-dom';
import {
  createIntegrationConnection,
  getIntegrationConnectionById,
  updateIntegrationConnection
} from '../../../../../common/utils/integrations-client/jira/connections';
import { SupportedIntegrationProductSlugs } from '../../../../../common/constants';
import classNames from 'classnames';

export interface CustomSettingsForm {
  url: string;
  name: string;
  secret: string;
}

export interface CustomSettingsProps {
  activeEnterprise: v2.Enterprise;
}

interface RouteParams {
  id?: string;
}

function CustomSettings({ activeEnterprise }: CustomSettingsProps) {
  const { t } = useTranslation();

  const history = useHistory();
  const { id: connection_id } = useParams<RouteParams>();
  const createMode = !connection_id;

  // Get info required for making API calls
  const { user: authUser } = useAuthContext();

  // Get jira on prem settings for this enterprise
  const [settingsError, setSettingsError] = useState<ErrorOrNull>(null);
  const [settingsLoading, setSettingsLoading] = useState(!createMode);
  const [secretEditable, setSecretEditable] = useState(createMode);
  const [originalName, setOriginalNameEditable] = useState('');
  useEffect(() => {
    async function fetchSettings() {
      if (activeEnterprise && authUser && !createMode) {
        try {
          setSettingsLoading(true);
          const integrationConnection = await getIntegrationConnectionById({
            enterpriseId: activeEnterprise.id,
            token: authUser.token,
            connectionId: connection_id
          });

          setOriginalNameEditable(integrationConnection.name || '');
          reset({
            url: integrationConnection.base_url || '',
            name: integrationConnection.name || ''
          });
        } catch (error) {
          const errorMessage = t('Failed to load settings.');

          setSettingsError(new Error(errorMessage));
        } finally {
          setSettingsLoading(false);
        }
      }
    }

    fetchSettings();
  }, [activeEnterprise, authUser]);

  const { handleSubmit, reset, resetField, control, formState } =
    useForm<CustomSettingsForm>();

  const onSubmit = async (data: CustomSettingsForm) => {
    if (activeEnterprise && authUser) {
      try {
        setSettingsLoading(true);
        if (createMode) {
          await createIntegrationConnection({
            enterpriseId: activeEnterprise.id,
            token: authUser.token,
            body: {
              base_url: data.url,
              secret: data.secret,
              name: data.name,
              product_slug: SupportedIntegrationProductSlugs.customIntegration
            }
          });
        } else {
          await updateIntegrationConnection({
            enterpriseId: activeEnterprise.id,
            connectionId: connection_id,
            token: authUser.token,
            body: {
              base_url: data.url,
              secret: secretEditable ? data.secret : undefined,
              name: data.name
            }
          });
        }

        goBackToIntegrationsConfiguration();
      } catch (error) {
        const errorMessage = t('Failed to save settings.');

        setSettingsError(new Error(errorMessage));
      } finally {
        setSettingsLoading(false);
      }
    }
  };

  const goBackToIntegrationsConfiguration = () => {
    history.push('/configuration/integrations');
  };
  const resetUrl = () => {
    resetField('url');
  };

  const nameError = formState.errors.name
    ? formState.errors.name.type === 'required'
      ? t('Name is required.')
      : t('Name is too long, max length is 24 characters.')
    : undefined;
  const secretError = formState.errors.secret
    ? formState.errors.secret.type === 'required'
      ? t('Secret is required.')
      : t('Secret is too long, max length is 255 characters.')
    : undefined;

  const form = (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.textFieldGroup}>
      <Controller
        control={control}
        name="name"
        rules={{
          required: true,
          maxLength: 24
        }}
        render={({ field: { ref, ...field } }) => (
          <TextField
            {...field}
            fieldRef={ref}
            id="name"
            aria-label={t('Name')}
            label={t('Name')}
            required
            requiredText={t('Required')}
            error={
              nameError ? (
                <div className={styles.errorWithReset}>
                  <span>{nameError}</span>
                </div>
              ) : undefined
            }
          />
        )}
      />
      <div className={styles.urlFieldGroup}>
        <Controller
          control={control}
          name="url"
          rules={{
            required: true,
            validate: value => {
              try {
                const url = new URL(value);
                return url.protocol === 'https:';
              } catch {
                return false;
              }
            }
          }}
          render={({ field: { ref, ...field } }) => (
            <TextField
              {...field}
              fieldRef={ref}
              id="integration-url"
              aria-label={t('Integration Webhook URL')}
              label={t('Webhook URL')}
              required
              requiredText={t('Required')}
              aria-describedby="url-description"
              error={
                formState.errors.url ? (
                  <div className={styles.errorWithReset}>
                    <Trans>
                      <span>A valid https URL is required.</span>
                      <Button
                        id="reset-integration-url"
                        aria-label={t('Reset Integration Webhook URL')}
                        variant="link"
                        onClick={resetUrl}
                      >
                        Reset
                      </Button>
                    </Trans>
                  </div>
                ) : undefined
              }
            />
          )}
        />
        <label id="url-description" className={styles.urlDescription}>
          {t(
            'This is the endpoint that will receive a POST request when users click the "Send to..." button for this integration'
          )}
        </label>
      </div>
      <div className={classNames({ [styles.secretFieldGroup]: !createMode })}>
        {secretEditable ? (
          <Controller
            control={control}
            name="secret"
            rules={{
              required: true,
              maxLength: 255
            }}
            render={({ field: { ref, ...field } }) => (
              <TextField
                {...field}
                fieldRef={ref}
                id="secret"
                aria-label={t('Secret')}
                label={t('Secret')}
                required
                requiredText={t('Required')}
                error={
                  secretError ? (
                    <div className={styles.errorWithReset}>
                      <span>{secretError}</span>
                    </div>
                  ) : undefined
                }
              />
            )}
          />
        ) : (
          <div className="Field">
            <label
              className="Field__label Field__label--is-required"
              htmlFor="secret"
            >
              <span>Secret</span>
            </label>
            <span id="secret">**********</span>
          </div>
        )}

        {!createMode && (
          <Button
            onClick={() => setSecretEditable(!secretEditable)}
            variant="link"
            className={styles.secretToggle}
            aria-label={
              secretEditable
                ? t('Cancel updating integration secret')
                : t('Update integration secret')
            }
          >
            {secretEditable ? t('Cancel') : t('Update secret')}
          </Button>
        )}
      </div>
      <div className={styles.configurationFooter}>
        <Button disabled={false} onClick={handleSubmit(onSubmit)}>
          {t('Save')}
        </Button>
        <Button
          disabled={false}
          onClick={goBackToIntegrationsConfiguration}
          variant="secondary"
        >
          {t('Discard')}
        </Button>
      </div>
    </form>
  );

  return (
    <>
      <ColumnHeader>
        <h2>{t('Custom Integration Settings')}</h2>
      </ColumnHeader>

      <h3>
        {t(`{{name}} Settings`, {
          name: originalName ? originalName : 'New Custom Integration'
        })}
      </h3>

      {!settingsLoading || <ScrimmedLoader label={t('Loading ...')} />}

      {!settingsError || (
        <ContentToast show={true} type="caution">
          {settingsError?.message || t('Failed to load settings information.')}
        </ContentToast>
      )}
      {!settingsLoading && form}
    </>
  );
}

export default CustomSettings;
